import { useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { Home } from "../pages/Home";
import { getStoreBaseUrl, getStorePathUrl } from "../lib/storeUrls";

type StoreRedirectProps = {
  /** Forward the current path to the store (e.g. `/shop/...`). Default: store home. */
  preservePath?: boolean;
  /** Absolute URL to open. Used for apex/www `/` → store.balabalabom.com. */
  href?: string;
};

/**
 * Hands visitors from client-public to client-store.
 * Redirects in useLayoutEffect (before paint) to avoid a homepage flash.
 */
export function StoreRedirect({ preservePath = false, href }: StoreRedirectProps) {
  const location = useLocation();
  const target =
    href ??
    (preservePath
      ? getStorePathUrl(`${location.pathname}${location.search}`)
      : getStoreBaseUrl());

  useLayoutEffect(() => {
    if (!target) return;
    window.location.replace(target);
  }, [target]);

  if (!target) {
    if (preservePath) {
      return (
        <main style={{ maxWidth: "36rem", margin: "3rem auto", padding: "0 1.25rem" }}>
          <p>The shop is not configured for this site.</p>
        </main>
      );
    }
    return <Home />;
  }

  // Blank while navigating — do not paint header copy / interstitial.
  return (
    <main
      aria-busy="true"
      aria-live="polite"
      style={{
        minHeight: "40vh",
        margin: 0,
        padding: "2rem 1.25rem",
        backgroundColor: "#fdfbf7",
      }}
    >
      <span
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        Redirecting to the shop
      </span>
    </main>
  );
}
